import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { AuthNavigationService } from './auth-navigation.service';

@Injectable({
  providedIn: 'root'
})
export class UserContextService {
  private _sourceId: string | null = null;
  private _isAgVantisUser: boolean = false;
  private _organizationIds: string[] = [];

  constructor(
    private authService: AuthService,
    private authNav: AuthNavigationService
  ) {}

  get sourceId(): string | null {
    return this._sourceId;
  }

  set sourceId(value: string | null) {
    this._sourceId = value;
  }

  get isAgVantisUser(): boolean {
    return this._isAgVantisUser;
  }

  set isAgVantisUser(value: boolean) {
    this._isAgVantisUser = value;
  }

  /** Tenant/organization IDs from the access token (for selection screen when multiple). */
  get organizationIds(): string[] {
    return [...this._organizationIds];
  }

  hasDataReadRole(): boolean {
    return this.authService.hasDataReadRole();
  }

  hasDataWriteRole(): boolean {
    return this.authService.hasDataWriteRole();
  }

  hasAccess(): boolean {
    return this.authService.hasAccess();
  }

  /** True if user has no tenants assigned (should see "No tenants" message). */
  get hasNoTenants(): boolean {
    return this._organizationIds.length === 0;
  }

  /** True if user should see tenant selection (AgVantis = all tenants, or multiple org IDs). */
  get shouldShowTenantSelection(): boolean {
    return this._isAgVantisUser || this._organizationIds.length > 1;
  }

  async initializeAsync(): Promise<void> {
    await this.authService.ensureAuthenticatedAsync();

    if (!this.authService.hasAccess()) {
      return;
    }

    this._organizationIds = this.authService.getOrganizationIds();
    this._isAgVantisUser = await this.authService.isAgVantisUserAsync();

    if (this._isAgVantisUser) {
      return;
    }

    if (this._organizationIds.length > 1) {
      return;
    }

    if (this._organizationIds.length === 1) {
      this._sourceId = this._organizationIds[0];
      return;
    }
  }

  setSourceId(sourceId: string): void {
    this._sourceId = sourceId;
  }

  async ensureSourceIdAsync(): Promise<string | null> {
    if (this._sourceId) {
      return this._sourceId;
    }

    try {
      await this.initializeAsync();
      if (this._sourceId) {
        return this._sourceId;
      }
    } catch {
      // continue
    }

    if (this.hasNoTenants) {
      this.authNav.navigateToNotAuthorizedNoTenants();
      return null;
    }

    if (this.shouldShowTenantSelection) {
      this.authNav.navigateToAssociationSelection();
      return null;
    }

    this._sourceId = await this.authService.getSourceIdAsync();
    return this._sourceId;
  }
}
